import { ArrowDown, ArrowUp } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface IHeadCell {
    id: string;
    name?: string | React.JSX.Element;
    rowClassName?: string;
    visible?: boolean;
    sortable?: boolean;
    headCellsTextClass?: string;
}

interface IProps {
    headCells?: IHeadCell[];
    params?: any;
    setParams?: any;
    tableDataArr: any;
    TableLoaderRowCmp?: any;
    TableRowCmp: any;
    tableRowCmpProps?: any;
    isTableDataLoading?: boolean;
    isTableDataLoadingWithData?: boolean;
    onRowClick?: any;
    isTableRowClickable?: boolean;
    isTableRowClickableCondition?: any;
    trCustomClass?: boolean;
    numberOfSkeletonRows?: number;
    tableHeaderClass?: string;
    tableFooterRowClass?: string;
    footerDataArr?: any;
    TableFooterRowCmp?: any;
    tableFooterRowCmpProps?: any;
    tableFooterClass?: string;
    isTableFooterSticky?: boolean;
    isTableFooterShow?: boolean;
    trCustomBaseHeight?: number;
    totalHeight?: string;
    tableBodyRowClass?: string;
    getRowClass?: any;
    isDisplaySubData?: boolean;
    SubDataCmp?: any;
    selectedData?: any;
    selectedRows?: any;
    subDataRowCmpProps?: any;
    tableWrapClass?: string;
    onMouseEnter?: any;
    onMouseLeave?: any;
    tableSortingOnClick?: any;
    tableRowHeight?: string;
    isTableSpacingWide?: boolean;
    isTableMetaData?: boolean;
    isBulkBarOpen?: boolean;
    isActionColumnSticky?: boolean;
    isFirstColumnSticky?: boolean;
}

const TableCmp = ({
    headCells,
    params,
    setParams,
    tableDataArr,
    TableLoaderRowCmp,
    TableRowCmp,
    tableRowCmpProps = {},
    isTableDataLoading,
    isTableDataLoadingWithData,
    onRowClick,
    isTableRowClickable,
    isTableRowClickableCondition,
    trCustomClass,
    tableWrapClass,
    tableHeaderClass,
    tableFooterClass,
    tableFooterRowClass,
    numberOfSkeletonRows = 5,
    TableFooterRowCmp,
    footerDataArr = [],
    tableFooterRowCmpProps = {},
    isTableFooterSticky = false,
    isTableFooterShow = false,
    trCustomBaseHeight,
    tableBodyRowClass,
    totalHeight,
    getRowClass,
    isDisplaySubData = false,
    SubDataCmp,
    selectedData,
    selectedRows,
    subDataRowCmpProps = {},
    onMouseEnter,
    onMouseLeave,
    tableSortingOnClick,
    tableRowHeight,
    isTableSpacingWide,
    isTableMetaData,
    isBulkBarOpen = false,
    isActionColumnSticky = false,
    isFirstColumnSticky = false,
}: IProps) => {
    const tableRef = useRef<HTMLTableElement>(null);
    const [headCellsModified, setHeadCellsModified] = useState<IHeadCell[]>([]);

    useEffect(() => {
        if (!isActionColumnSticky) return;

        const el = tableRef.current;
        if (!el) return;

        let scrollEl: Element | null = el.parentElement;

        while (scrollEl) {
            const { overflowX } = getComputedStyle(scrollEl);
            if (overflowX === 'auto' || overflowX === 'scroll') break;
            scrollEl = scrollEl.parentElement;
        }
        if (!scrollEl) return;

        const checkScroll = () => {
            const { scrollLeft, scrollWidth, clientWidth } = scrollEl as HTMLElement;
            // +1 tolerance for sub-pixel widths so the shadow clears when content fits fully (no horizontal scroll)
            el.classList.toggle('action-shadow-active', scrollLeft + clientWidth < scrollWidth - 1);
        };

        scrollEl.addEventListener('scroll', checkScroll, { passive: true });
        // Recompute on resize: when the table/container resizes to fit fully no scroll event fires, so the shadow must be cleared here
        const resizeObserver = new ResizeObserver(checkScroll);
        resizeObserver.observe(scrollEl);
        resizeObserver.observe(el);
        checkScroll();

        return () => {
            scrollEl!.removeEventListener('scroll', checkScroll);
            resizeObserver.disconnect();
        };
    }, [isActionColumnSticky]);

    useEffect(() => {
        if (!isFirstColumnSticky) return;

        const el = tableRef.current;
        if (!el) return;

        let scrollEl: Element | null = el.parentElement;

        while (scrollEl) {
            const { overflowX } = getComputedStyle(scrollEl);
            if (overflowX === 'auto' || overflowX === 'scroll') break;
            scrollEl = scrollEl.parentElement;
        }
        if (!scrollEl) return;

        const checkScroll = () => {
            const { scrollLeft } = scrollEl as HTMLElement;
            el.classList.toggle('first-shadow-active', scrollLeft > 0);
        };

        scrollEl.addEventListener('scroll', checkScroll, { passive: true });
        // Recompute on resize: when the table/container resizes to fit fully no scroll event fires, so the shadow must be cleared here
        const resizeObserver = new ResizeObserver(checkScroll);
        resizeObserver.observe(scrollEl);
        resizeObserver.observe(el);
        checkScroll();

        return () => {
            scrollEl!.removeEventListener('scroll', checkScroll);
            resizeObserver.disconnect();
        };
    }, [isFirstColumnSticky]);
    useLayoutEffect(() => {
        const modifiedHead = headCells?.map((cell) => ({
            ...cell,
            visible: cell.visible !== undefined ? cell.visible : true,
        }));
        if (modifiedHead) setHeadCellsModified(modifiedHead);
    }, [headCells]);

    const calculateHeight = (length: any, index: any) => {
        if (length <= 12 && index === length - 1 && trCustomBaseHeight) {
            let baseHeightDecrementNumber = trCustomBaseHeight;
            const additionalHeight = (length - 1) * 42;
            baseHeightDecrementNumber += additionalHeight;

            return `calc(${totalHeight} - ${baseHeightDecrementNumber}px)`;
        }

        return 'auto';
    };

    return (
        <>
            <table
                ref={tableRef}
                className={`table text-sm text-left text-nowrap whitespace-nowrap ${tableWrapClass} ${isActionColumnSticky ? 'sticky-action-column' : ''} ${
                    isFirstColumnSticky ? 'sticky-first-column' : ''
                }`}
            >
                {headCellsModified.length > 0 && (
                    <thead className={`text-xs leading-[1.5] text-grayLight600 font-medium bg-gray50  ${isBulkBarOpen && 'opacity-0'} ${tableHeaderClass}`}>
                        <tr
                            className={`border-b border-utilityGray200 bg-inherit ${
                                tableRowHeight === 'xs' || isFirstColumnSticky || isActionColumnSticky ? '[&:last-child]:table-header-col-height-xs' : ''
                            } `}
                        >
                            {headCellsModified.map((head: IHeadCell, index: any) => (
                                <th
                                    scope="col"
                                    className={`${head.rowClassName || ''} ${
                                        tableRowHeight === 'xs' ? 'px-1.5 first:pl-[var(--table-col-space-common-xs-leftrightmost)] last:pr-[var(--table-col-space-common-xs-leftrightmost)]' : 'px-3 '
                                    }  py-[9px] font-medium ${head.sortable ? 'cursor-pointer' : ''} ${head.visible ? '' : 'hidden'} ${isActionColumnSticky ? '[&:last-child]:!p-0' : ''} ${
                                        isFirstColumnSticky ? '[&:first-child]:!p-0 [&:first-child>div]:!px-[var(--table-col-space-common-xs-leftrightmost)]' : ''
                                    }`}
                                    key={index}
                                    onClick={() => {
                                        if (isTableDataLoading) return;

                                        if (head.sortable && setParams) {
                                            if (tableSortingOnClick) {
                                                tableSortingOnClick();
                                            }
                                            setParams((old: any) => ({
                                                ...old,
                                                sortType: params.sortType === 'desc' && params.sortField === head.id ? 'asc' : 'desc',
                                                sortField: head.id,
                                            }));
                                        }
                                    }}
                                >
                                    <div className={`flex items-center ${head.headCellsTextClass || ''} ${isActionColumnSticky || isFirstColumnSticky ? '!h-full' : ''} `}>
                                        <span>{head.name}</span>
                                        {head.sortable && params && (
                                            <span className={`${tableDataArr?.length > 0 ? '' : 'opacity-0'}`}>
                                                {params.sortField === head.id ? (
                                                    <>
                                                        {params.sortType === 'desc' ? (
                                                            <ArrowDown className={`w-4 h-4 cursor-pointer flex-none ms-1`} />
                                                        ) : (
                                                            <ArrowUp className={`w-4 h-4 cursor-pointer flex-none ms-1`} />
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        {params.sortType === 'desc' ? (
                                                            <ArrowUp className={`w-4 h-4 cursor-pointer flex-none ms-1 text-gray-300`} />
                                                        ) : (
                                                            <ArrowDown className={`w-4 h-4 cursor-pointer flex-none ms-1 text-gray-300`} />
                                                        )}
                                                    </>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                )}

                <tbody>
                    {tableDataArr.length > 0 ? (
                        <>
                            {tableDataArr.map((data: any, index: any) => (
                                <React.Fragment key={`${data.id || index}_${data?.contactType || ''}_${params?.page ?? 'PAGE_NUMBER'}`}>
                                    <tr
                                        key={`${data.id || index}_${data?.contactType || ''}_${params?.page ?? 'PAGE_NUMBER'}`}
                                        className={`text-grayLight600 border-b border-utilityGray200 font-normal text-xs bg-transparent transition-all duration-[0.5s] table-icon-on-hover ${
                                            isTableSpacingWide
                                                ? '[&>td]:p-4 table-col-space-common-sm'
                                                : isTableMetaData
                                                ? 'table-col-space-metadata'
                                                : tableRowHeight === 'xs'
                                                ? 'table-col-space-common-xs'
                                                : 'table-col-space-common'
                                        } ${tableRowHeight === 'xs' ? 'table-col-height-xs' : tableRowHeight === 'sm' ? 'table-col-height-sm' : 'table-col-height-md'} ${tableBodyRowClass} ${
                                            data.hasUnreadNotification ? 'notification-row' : ''
                                        } ${getRowClass ? getRowClass(data) : ''} ${
                                            (isTableRowClickableCondition ? isTableRowClickableCondition(data) : isTableRowClickable) ? 'cursor-pointer hover:bg-utilityGray100' : ''
                                        }`}
                                        style={{
                                            height: trCustomClass && data.id == 0 ? calculateHeight(tableDataArr.length, index) : '',
                                        }}
                                        onClick={() => {
                                            const isClickable = isTableRowClickableCondition ? isTableRowClickableCondition(data) : isTableRowClickable;

                                            if (isClickable && onRowClick) {
                                                onRowClick(data);
                                            }
                                        }}
                                        onMouseEnter={() => onMouseEnter && onMouseEnter(data)}
                                        onMouseLeave={() => onMouseLeave && onMouseLeave(data)}
                                    >
                                        <TableRowCmp data={data} currentIndex={index} {...tableRowCmpProps} />
                                    </tr>
                                    {isDisplaySubData && selectedData?.id === data?.id && <SubDataCmp {...subDataRowCmpProps} data={data} />}

                                    {isDisplaySubData && selectedRows && selectedRows[data.id] && <SubDataCmp data={data} currentIndex={index} {...subDataRowCmpProps} />}
                                </React.Fragment>
                            ))}
                            {isTableDataLoadingWithData
                                ? [...Array(numberOfSkeletonRows)].map((_, index) => (
                                      <tr
                                          key={index}
                                          className={`text-grayLight900 text-xs font-normal border-b border-gray100 ${
                                              isTableSpacingWide
                                                  ? '[&>td]:p-4 table-col-space-common-sm'
                                                  : isTableMetaData
                                                  ? 'table-col-space-metadata'
                                                  : tableRowHeight === 'xs'
                                                  ? 'table-col-space-common-xs'
                                                  : 'table-col-space-common'
                                          } ${tableRowHeight === 'xs' ? 'table-col-height-xs' : tableRowHeight === 'sm' ? 'table-col-height-sm' : 'table-col-height-md '} ${tableBodyRowClass}`}
                                      >
                                          <TableLoaderRowCmp key={index} {...tableRowCmpProps} />
                                      </tr>
                                  ))
                                : false}
                        </>
                    ) : isTableDataLoading ? (
                        [...Array(numberOfSkeletonRows)].map((_, index) => (
                            <tr
                                key={index}
                                className={`text-grayLight900 text-xs font-normal border-b border-gray100 ${
                                    isTableSpacingWide
                                        ? '[&>td]:p-4 table-col-space-common-sm'
                                        : isTableMetaData
                                        ? 'table-col-space-metadata'
                                        : tableRowHeight === 'xs'
                                        ? 'table-col-space-common-xs'
                                        : 'table-col-space-common'
                                } ${tableRowHeight === 'xs' ? 'table-col-height-xs' : tableRowHeight === 'sm' ? 'table-col-height-sm' : 'table-col-height-md '} ${tableBodyRowClass}`}
                            >
                                <TableLoaderRowCmp key={index} {...tableRowCmpProps} />
                            </tr>
                        ))
                    ) : (
                        false
                    )}
                </tbody>
                {!isTableFooterSticky && !isTableDataLoading && tableDataArr.length > 0 && footerDataArr?.length > 0 && (
                    <tfoot className={`${tableFooterClass}`}>
                        {footerDataArr.map((data: any, index: any) => (
                            <tr
                                key={index}
                                className={`text-grayLight900 text-xs font-normal border-b border-utilityGray200 ${
                                    isTableSpacingWide ? '[&>td]:p-4 table-col-space-common-sm' : isTableMetaData ? 'table-col-space-metadata' : 'table-col-space-common'
                                } ${tableRowHeight === 'xs' ? 'table-col-height-xs' : tableRowHeight === 'sm' ? 'table-col-height-sm' : 'table-col-height-md'} ${tableFooterRowClass}`}
                            >
                                <TableFooterRowCmp data={data} {...tableFooterRowCmpProps} />
                            </tr>
                        ))}
                    </tfoot>
                )}
                {isTableFooterShow && footerDataArr?.length > 0 && (
                    <tfoot className={`${tableFooterClass}`}>
                        {footerDataArr.map((data: any, index: any) => (
                            <tr
                                key={index}
                                className={`text-grayLight900 text-xs font-normal border-b border-utilityGray200 ${
                                    isTableSpacingWide ? '[&>td]:p-4 table-col-space-common-sm' : isTableMetaData ? 'table-col-space-metadata' : 'table-col-space-common'
                                } ${tableRowHeight === 'xs' ? 'table-col-height-xs' : tableRowHeight === 'sm' ? 'table-col-height-sm' : 'table-col-height-md'} ${tableFooterRowClass}`}
                            >
                                <TableFooterRowCmp data={data} {...tableFooterRowCmpProps} />
                            </tr>
                        ))}
                    </tfoot>
                )}
            </table>
            {isTableFooterSticky && TableFooterRowCmp && tableDataArr.length > 0 && (
                <table className="table text-sm text-left text-nowrap whitespace-nowrap sticky bottom-0">
                    <thead className={`text-xs text-grayLight600 font-medium bg-gray50 invisible ${tableHeaderClass}`}>
                        <tr className="border-b border-utilityGray200">
                            {headCellsModified.map((head: IHeadCell, index: any) => (
                                <th
                                    scope="col"
                                    className={`${head.rowClassName} px-5 py-0 font-medium ${head.sortable && tableDataArr?.length > 0 ? 'cursor-pointer' : ''} ${head.visible ? '' : 'hidden'}`}
                                    key={index}
                                    onClick={() => {
                                        if (head.sortable && setParams && tableDataArr?.length > 0) {
                                            if (tableSortingOnClick) {
                                                tableSortingOnClick();
                                            }
                                            setParams((old: any) => ({
                                                ...old,
                                                sortType: params.sortType === 'desc' && params.sortField === head.id ? 'asc' : 'desc',
                                                sortField: head.id,
                                            }));
                                        }
                                    }}
                                >
                                    <div className={`flex items-center ${head.headCellsTextClass || ''}`}>
                                        <span className="leading-[0]">{head.name}</span>
                                        {head.sortable && params && (
                                            <>
                                                {params.sortField === head.id ? (
                                                    <>
                                                        {params.sortType === 'desc' ? (
                                                            <ArrowDown className="w-4 h-0 cursor-pointer flex-none ms-1" />
                                                        ) : (
                                                            <ArrowUp className="w-4 h-0 cursor-pointer flex-none ms-1" />
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        {params.sortType === 'desc' ? (
                                                            <ArrowUp className="w-4 h-0 cursor-pointer flex-none ms-1 text-gray-300" />
                                                        ) : (
                                                            <ArrowDown className="w-4 h-0 cursor-pointer flex-none ms-1 text-gray-300" />
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    {footerDataArr.length > 0 && (
                        <tfoot className={`${tableFooterClass}`}>
                            {footerDataArr.map((data: any, index: any) => (
                                <tr key={index} className={`text-grayLight900 text-xs font-normal border-b border-utilityGray200 ${tableFooterRowClass}`}>
                                    <TableFooterRowCmp data={data} {...tableFooterRowCmpProps} />
                                </tr>
                            ))}
                        </tfoot>
                    )}
                </table>
            )}
        </>
    );
};

export default TableCmp;
